import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Search, MessageSquarePlus } from "lucide-react";
import { toast } from "sonner";

const ROLE_BADGE = {
  anesthetist: "bg-blue-50 text-blue-700",
  admin: "bg-amber-50 text-amber-800",
  surgeon: "bg-purple-50 text-purple-700",
  patient: "bg-green-50 text-green-700",
};

export default function ContactsDialog({ endpoint, onStarted, label = "New conversation" }) {
  const [open, setOpen] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => {
    if (open) api.get(endpoint).then((r) => setContacts(r.data || [])).catch(() => {});
  }, [open, endpoint]);

  const start = async (uid) => {
    try {
      const { data } = await api.post("/conversations/start", { recipient_id: uid });
      toast.success("Conversation started");
      setOpen(false);
      onStarted?.(data.conversation_id);
    } catch {
      toast.error("Could not start conversation");
    }
  };

  const filtered = contacts.filter((c) => {
    const t = `${c.name} ${c.email}`.toLowerCase();
    return !q || t.includes(q.toLowerCase());
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid="contacts-open-btn"><MessageSquarePlus className="h-4 w-4 mr-1.5" />{label}</Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Start a conversation</DialogTitle></DialogHeader>
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input className="pl-8" placeholder="Search by name or email…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="contacts-search" />
        </div>
        <ScrollArea className="max-h-80">
          {filtered.length === 0 ? (
            <div className="p-6 text-sm text-slate-400 text-center">No contacts</div>
          ) : (
            <ul>
              {filtered.map((c) => {
                const initials = (c.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
                return (
                  <li key={c.user_id} className="border-b border-slate-100 last:border-b-0">
                    <button onClick={() => start(c.user_id)} data-testid={`contact-${c.user_id}`}
                      className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50 text-left transition-colors">
                      <Avatar className="h-9 w-9"><AvatarFallback className="bg-[#E0A96D]/30 text-[#339999] text-xs font-semibold">{initials}</AvatarFallback></Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-900 truncate">{c.name}</div>
                        <div className="text-xs text-slate-500 truncate">{c.email}</div>
                      </div>
                      <span className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ${ROLE_BADGE[c.role] || "bg-slate-100 text-slate-600"}`}>{c.role}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
